import { REALTIME } from "../config/global.configuration";
import { requestGetMsgpack, requestPostMsgpack } from "../tools/HttpTools";
import type {
    BBox,
    InitResponse,
    SnapshotResponse,
    TripDelayWire,
    TripDetailResponse,
} from "../types/dtos/realtime-api/realtime.dto";

class RealtimeService {

    /**
     * Fetches the realtime init payload used to build the local trip index.
     *
     * MessagePack only (`Accept: application/msgpack`).
     *
     * @returns Init payload containing trip index entries.
     */
    async fetchInit(): Promise<InitResponse> {
        return requestGetMsgpack<InitResponse>( `${ REALTIME.apiBase }/init` );
    }

    /**
     * Fetches a viewport-scoped realtime snapshot with delta hydration.
     *
     * MessagePack only. Shape geometry is `points_bin` (MessagePack `bin` per shape).
     *
     * @param bbox Current expanded viewport bounds.
     * @param timestamp Client timestamp in milliseconds.
     * @param knownShapes Shape identifiers already cached on the client.
     * @param knownTrips Trip identifiers already cached on the client.
     * @returns Snapshot delta payload.
     */
    async fetchSnapshot(
        bbox: BBox,
        timestamp: number,
        knownShapes: Set<string>,
        knownTrips: Set<string>,
    ): Promise<SnapshotResponse> {
        const bboxStr = `${ bbox.minLat },${ bbox.minLon },${ bbox.maxLat },${ bbox.maxLon }`;

        return requestPostMsgpack<SnapshotResponse>(
            `${ REALTIME.apiBase }/snapshot`,
            {
                "known_shapes": [ ...knownShapes ],
                "known_trips":  [ ...knownTrips ],
            },
            {
                "params": {
                    "bbox": bboxStr,
                    "t":    timestamp,
                },
            },
        );
    }

    /**
     * Fetches full schedule and timeline details for a single trip.
     *
     * MessagePack only. Shape geometry is `shape.points_bin` (14-byte LE layout).
     *
     * @param tripId Trip identifier.
     * @returns Detailed trip payload.
     */
    async fetchTripDetail( tripId: string ): Promise<TripDetailResponse> {
        return requestGetMsgpack<TripDetailResponse>( `${ REALTIME.apiBase }/trip/${ encodeURIComponent( tripId ) }` );
    }

    /**
     * Fetches delay data for the provided trip identifiers.
     *
     * MessagePack only.
     *
     * @param tripIds Trip identifiers to query.
     * @returns Delay payloads for matching trips.
     */
    async fetchDelays( tripIds: string[] ): Promise<TripDelayWire[]> {
        return requestGetMsgpack<TripDelayWire[]>(
            `${ REALTIME.apiBase }/delays`,
            { "params": { "trips": tripIds.join( "," ) } },
        );
    }

}

export const realtimeService = new RealtimeService();
